import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ProgramsPage from "@/pages/ProgramsPage";
import CoursesPage from "@/pages/CoursesPage";
import OnlineCoursesPage from "@/pages/OnlineCoursesPage";
import ContactPage from "@/pages/ContactPage";
import FranchisePage from "@/pages/FranchisePage";
import FranchiseApplyPage from "@/pages/FranchiseApplyPage";
import PlacementsPage from "@/pages/PlacementsPage";
import BlogPage from "@/pages/BlogPage";
import CertificateVerificationPage from "@/pages/CertificateVerificationPage";

// Auth Pages
import StudentRegisterPage from "@/pages/auth/StudentRegisterPage";
import EmployerRegisterPage from "@/pages/auth/EmployerRegisterPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import UnifiedLoginPage from "@/pages/auth/UnifiedLoginPage";
import UnifiedRegisterPage from "@/pages/auth/UnifiedRegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";

// Student Portal
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentCourses from "@/pages/student/StudentCourses";
import StudentAssessments from "@/pages/student/StudentAssessments";
import StudentCertificates from "@/pages/student/StudentCertificates";
import CVBuilderPage from "@/pages/student/CVBuilderPage";
import StudentProfilePage from "@/pages/student/StudentProfilePage";
import AdmissionPage from "@/pages/student/AdmissionPage";
import LearningPage from "@/pages/student/LearningPage";
import StudentExamsPage from "@/pages/student/StudentExamsPage";
import OnlineExamPage from "@/pages/student/OnlineExamPage";
import DynamicExamPage from "@/pages/student/DynamicExamPage";
import ExamResultDetail from "@/pages/student/ExamResultDetail";
import PlacementDashboard from "@/pages/student/PlacementDashboard";

// Job Portal
import JobPortal from "@/pages/jobs/JobPortal";
import JobDetails from "@/pages/jobs/JobDetails";
import EmployerDashboard from "@/pages/jobs/EmployerDashboard";

// Employer Pages
import EmployerProfilePage from "@/pages/employer/EmployerProfilePage";

// Admin/Center Dashboard
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboardHome from "@/pages/admin/AdminDashboardHome";
import AdminFranchisePage from "@/pages/admin/AdminFranchisePage";
import AdminStudentsPage from "@/pages/admin/AdminStudentsPage";
import AdminCoursesPage from "@/pages/admin/AdminCoursesPage";
import AdminActivityLogsPage from "@/pages/admin/AdminActivityLogsPage";
import AdminEmployersPage from "@/pages/admin/AdminEmployersPage";
import AdminPlacementsPage from "@/pages/admin/AdminPlacementsPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminFinancePage from "@/pages/admin/AdminFinancePage";
import AdminLMSPage from "@/pages/admin/AdminLMSPage";
import AdminExamsPage from "@/pages/admin/AdminExamsPage";
import AdminQuestionBankPage from "@/pages/admin/AdminQuestionBankPage";
import AdminCertificatesPage from "@/pages/admin/AdminCertificatesPage";
import AdminCertificateSettingsPage from "@/pages/admin/AdminCertificateSettingsPage";
import AdminRolesPage from "@/pages/admin/AdminRolesPage";
import AdminSubAdminsPage from "@/pages/admin/AdminSubAdminsPage";
import AdminLogsPage from "@/pages/admin/AdminLogsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";

// Franchise Center Pages
import FranchisePaymentPage from "@/pages/FranchisePaymentPage";
import CenterDashboard from "@/pages/center/CenterDashboard";
import PreAffiliationDashboard from "@/pages/center/PreAffiliationDashboard";
import StageBasedCenterDashboard from "@/pages/center/StageBasedCenterDashboard";

// Center Dashboard Pages (New Premium)
import CenterLayout from "@/components/center/CenterLayout";
import CenterDashboardHome from "@/pages/center/CenterDashboardHome";
import CenterStudentsPage from "@/pages/center/CenterStudentsPage";
import CenterCoursesPage from "@/pages/center/CenterCoursesPage";
import CenterBatchesPage from "@/pages/center/CenterBatchesPage";
import CenterAttendancePage from "@/pages/center/CenterAttendancePage";
import CenterFinancePage from "@/pages/center/CenterFinancePage";
import CenterCRMPage from "@/pages/center/CenterCRMPage";
import CenterLMSPage from "@/pages/center/CenterLMSPage";
import CenterReportsPage from "@/pages/center/CenterReportsPage";
import CenterStaffPage from "@/pages/center/CenterStaffPage";
import CenterSettingsPage from "@/pages/center/CenterSettingsPage";
import CenterExamsPage from "@/pages/center/CenterExamsPage";
import CenterCertificatesPage from "@/pages/center/CenterCertificatesPage";

// Print Pages (standalone, no chrome)
import PrintStaffIdCard from "@/pages/print/PrintStaffIdCard";
import PrintStudentIdCard from "@/pages/print/PrintStudentIdCard";

// Premium Certificate System
import CertificateViewPage from "@/pages/certificates/CertificateViewPage";
import CertificateVerifyPage from "@/pages/certificates/CertificateVerifyPage";

// Assessment Portal
import AssessmentPortalPage from "@/pages/assessment/AssessmentPortalPage";
import ExamPage from "@/pages/assessment/ExamPage";
import ResultsPage from "@/pages/assessment/ResultsPage";
import CertificatePage from "@/pages/assessment/CertificatePage";
import MockTestPage from "@/pages/assessment/MockTestPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor to add auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = React.createContext({
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
  isAuthenticated: false
});

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, isAuthenticated } = React.useContext(AuthContext);
  
  // Check for sub-admin in localStorage (sub-admins have their own auth flow)
  const subAdminInfo = localStorage.getItem('sub_admin_info');
  const isSubAdmin = !!subAdminInfo;
  
  // If sub-admin and trying to access admin routes, allow
  if (isSubAdmin && roles && roles.includes('admin')) {
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (roles && !roles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    }
  };

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('auth_token', authToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
  };

  const authValue = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={authValue}>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/online-courses" element={<OnlineCoursesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/partner" element={<FranchisePage />} />
            <Route path="/franchise" element={<Navigate to="/partner" replace />} />
            <Route path="/partner-apply" element={<FranchiseApplyPage />} />
            <Route path="/franchise-apply" element={<Navigate to="/partner-apply" replace />} />
            <Route path="/franchise-payment" element={<FranchisePaymentPage />} />
            <Route path="/placements" element={<PlacementsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/certificate-verification" element={<CertificateVerificationPage />} />
            
            {/* Premium Certificate Routes */}
            <Route path="/certificate/:certificateId" element={<CertificateViewPage />} />
            <Route path="/certificate/dynamic/:attemptId" element={<CertificateViewPage />} />
            <Route path="/verify/:certificateNumber" element={<CertificateVerifyPage />} />

            {/* Standalone Print Routes (no sidebar / no header) */}
            <Route path="/print/staff-id/:staffId" element={<PrintStaffIdCard />} />
            <Route path="/print/student-id/:studentId" element={<PrintStudentIdCard />} />
            
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Unified Auth Routes */}
            <Route path="/login" element={<Navigate to="/auth/login" replace />} />
            <Route path="/auth/login" element={<UnifiedLoginPage />} />
            <Route path="/auth/register" element={<UnifiedRegisterPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Franchise Center Routes */}
            <Route path="/center/login" element={<Navigate to="/auth/login" replace />} />
            <Route path="/center/pre-affiliation" element={<PreAffiliationDashboard />} />
            <Route path="/center/stage-dashboard" element={<StageBasedCenterDashboard />} />
            
            {/* Premium Center Dashboard Routes */}
            <Route 
              path="/center"
              element={<CenterLayout />}
            >
              <Route path="dashboard" element={<CenterDashboardHome />} />
              <Route path="students" element={<CenterStudentsPage />} />
              <Route path="courses" element={<CenterCoursesPage />} />
              <Route path="batches" element={<CenterBatchesPage />} />
              <Route path="attendance" element={<CenterAttendancePage />} />
              <Route path="finance" element={<CenterFinancePage />} />
              <Route path="crm" element={<CenterCRMPage />} />
              <Route path="lms" element={<CenterLMSPage />} />
              <Route path="reports" element={<CenterReportsPage />} />
              <Route path="staff" element={<CenterStaffPage />} />
              <Route path="exams" element={<CenterExamsPage />} />
              <Route path="certificates" element={<CenterCertificatesPage />} />
              <Route path="settings" element={<CenterSettingsPage />} />
              {/* Redirect /center to /center/dashboard */}
              <Route index element={<CenterDashboardHome />} />
            </Route>
            
            {/* New Registration Routes */}
            <Route path="/register/student" element={<StudentRegisterPage />} />
            <Route path="/register/employer" element={<EmployerRegisterPage />} />

            {/* Job Portal */}
            <Route path="/jobs" element={<JobPortal />} />
            <Route path="/jobs/:jobId" element={<JobDetails />} />

            {/* Student Portal - Protected */}
            <Route 
              path="/student/dashboard" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/courses" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentCourses />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/assessments" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentAssessments />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/certificates" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentCertificates />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/cv-builder" 
              element={
                <ProtectedRoute roles={['student']}>
                  <CVBuilderPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/profile" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/admission" 
              element={
                <ProtectedRoute roles={['student']}>
                  <AdmissionPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/learn/:enrollmentId" 
              element={
                <ProtectedRoute roles={['student']}>
                  <LearningPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/exams" 
              element={
                <ProtectedRoute roles={['student']}>
                  <StudentExamsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/exam/:examId" 
              element={
                <ProtectedRoute roles={['student']}>
                  <OnlineExamPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/dynamic-exam" 
              element={
                <ProtectedRoute roles={['student']}>
                  <DynamicExamPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/exam-result/:attemptId" 
              element={
                <ProtectedRoute roles={['student']}>
                  <ExamResultDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/placements" 
              element={
                <ProtectedRoute roles={['student']}>
                  <PlacementDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Employer Dashboard - Protected */}
            <Route 
              path="/employer/dashboard" 
              element={
                <ProtectedRoute roles={['employer']}>
                  <EmployerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/employer/profile" 
              element={
                <ProtectedRoute roles={['employer']}>
                  <EmployerProfilePage />
                </ProtectedRoute>
              } 
            />

            {/* Admin Dashboard - Protected with Nested Routes */}
            <Route 
              path="/admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<AdminDashboardHome />} />
              <Route path="franchise" element={<AdminFranchisePage />} />
              <Route path="students" element={<AdminStudentsPage />} />
              <Route path="courses" element={<AdminCoursesPage />} />
              <Route path="activity-logs" element={<AdminActivityLogsPage />} />
              <Route path="employers" element={<AdminEmployersPage />} />
              <Route path="placements" element={<AdminPlacementsPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="finance" element={<AdminFinancePage />} />
              <Route path="lms" element={<AdminLMSPage />} />
              <Route path="exams" element={<AdminExamsPage />} />
              <Route path="question-bank" element={<AdminQuestionBankPage />} />
              <Route path="certificates" element={<AdminCertificatesPage />} />
              <Route path="certificate-settings" element={<AdminCertificateSettingsPage />} />
              <Route path="roles" element={<AdminRolesPage />} />
              <Route path="sub-admins" element={<AdminSubAdminsPage />} />
              <Route path="logs" element={<AdminLogsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              {/* Redirect /admin to /admin/dashboard */}
              <Route index element={<AdminDashboardHome />} />
            </Route>

            {/* Assessment Portal */}
            <Route path="/assessment" element={<AssessmentPortalPage />} />
            <Route 
              path="/assessment/exam/:courseId/:enrollmentId" 
              element={
                <ProtectedRoute roles={['student', 'admin']}>
                  <ExamPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assessment/results/:attemptId" 
              element={
                <ProtectedRoute roles={['student', 'admin']}>
                  <ResultsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assessment/certificate/:certificateId" 
              element={
                <ProtectedRoute roles={['student', 'admin']}>
                  <CertificatePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assessment/mock-test/:courseId" 
              element={
                <ProtectedRoute roles={['student', 'admin']}>
                  <MockTestPage />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
